import type { AssumedRole } from "@/contexts/AssumedRoleContext";

// Maps item keys to the roles allowed to see them.
// An absent key or empty array means visible to all roles (and to actual-role / null).
// When assumedRole is null (actual role), everything is visible — no filtering.
export const ROLE_PERMISSIONS: Record<string, AssumedRole[]> = {
  // ── Main trial nav ────────────────────────────────────────────────────────
  "/sites/enrollment": ["clinical-operations", "clinical-development"],
  "/sites/protocol-deviations": ["clinical-operations", "data-management"],

  "/safety": ["clinical-operations", "safety-medical-monitoring"],
  // Medical review is the medical monitor's own workflow; clinical operations
  // sees it because the monitoring plan usually names them as backup reviewer.
  "/safety/medical-review": [
    "safety-medical-monitoring",
    "clinical-operations",
  ],
  "/safety/adverse-events": [
    "clinical-operations",
    "safety-medical-monitoring",
  ],
  "/safety/dosing-tracker": [
    "clinical-operations",
    "safety-medical-monitoring",
  ],
  "/safety/ip-administration": [
    "clinical-operations",
    "safety-medical-monitoring",
  ],
  "/safety/pharmacokinetics": ["safety-medical-monitoring"],

  "/measures": ["clinical-science", "data-management"],
  "/measures/assessments": ["clinical-science", "data-management"],
  "/measures/imaging": ["clinical-science", "data-management"],
  "/measures/labs": ["clinical-science", "data-management"],
  "/measures/questionnaires": ["clinical-science", "data-management"],

  "/risk": ["clinical-operations", "risk-quality"],

  "/data": ["clinical-operations", "data-management"],

  // ── Site profile tabs ────────────────────────────────────────────────────
  milestones: ["clinical-operations", "clinical-development"],
  documents: ["clinical-operations"],
  "monitoring-visits": ["clinical-operations"],
  "adverse-events": ["clinical-operations", "safety-medical-monitoring"],
  "protocol-deviations": ["clinical-operations", "data-management"],
  "crf-completion": ["clinical-operations", "data-management"],
  discrepancies: ["clinical-operations", "data-management"],
  risk: ["clinical-operations", "risk-quality"],

  // ── Participant profile tabs ─────────────────────────────────────────────
  timeline: ["clinical-operations", "safety-medical-monitoring"],
  "ip-administration": ["clinical-operations", "safety-medical-monitoring"],
  "missed-dosing": ["clinical-operations", "safety-medical-monitoring"],
  pharmacokinetics: ["safety-medical-monitoring"],
  // adverse-events and protocol-deviations share the same perms as site tabs above
  "study-intervention": ["clinical-operations", "safety-medical-monitoring"],
  conmeds: ["clinical-operations", "safety-medical-monitoring"],
  assessments: ["clinical-science", "data-management"],
  imaging: ["clinical-science", "data-management"],
  labs: ["clinical-science", "data-management"],
  questionnaires: ["clinical-science", "data-management"],
  "data-discrepancies": [
    "clinical-operations",
    "clinical-science",
    "data-management",
  ],
  "medical-review": ["safety-medical-monitoring", "clinical-operations"],
};

export function isVisibleForRole(
  key: string,
  role: AssumedRole | null,
): boolean {
  if (role === null) return true; // actual role — no filtering
  const allowed = ROLE_PERMISSIONS[key];
  if (!allowed || allowed.length === 0) return true; // visible to all roles
  return allowed.includes(role);
}
